import React from 'react';
import PublicPageLayout from '../components/landing-page/PublicPageLayout';

export default function CookiesPage() {
  return (
    <PublicPageLayout
      title='Cookie Policy'
      subtitle='How PricingTable AI uses cookies and similar technologies on our websites and product.'
    >
      <p className='text-sm text-gray-400'>Last updated: August 7, 2025</p>

      <section className='space-y-3'>
        <h2 className='text-xl font-semibold text-white'>Introduction</h2>
        <p>
          This Cookie Policy describes how PricingTable AI (&quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) uses cookies and related
          technologies on our marketing website, web application, and related services that link to this policy (collectively, the
          &quot;Services&quot;). Our platform helps SaaS teams run pricing experiments: customers upload pricing tables, define price
          combinations to test, embed installation code on their sites, and use analytics tied to visitor sessions to learn which
          pricing variations perform best.
        </p>
        <p>
          By using the Services, you agree to the use of cookies as described here, except where you have adjusted your preferences
          or browser settings to limit non-essential cookies. For how we handle personal data more broadly, see our Privacy Policy.
        </p>
      </section>

      <section className='space-y-3'>
        <h2 className='text-xl font-semibold text-white'>What are cookies?</h2>
        <p>
          Cookies are small text files stored on your device when you visit a website or use a web application. They are widely used
          to make sites work more efficiently, remember preferences, keep you signed in, and understand how services are used.
        </p>
        <p>
          We may also use similar technologies such as local storage, session storage, and pixels, which can serve comparable
          functions. In this policy, &quot;cookies&quot; refers to cookies and these related technologies unless we say otherwise.
        </p>
        <p>
          <span className='font-medium text-white'>First-party cookies</span> are set by us.{' '}
          <span className='font-medium text-white'>Third-party cookies</span> are set by partners we use (for example, analytics or
          infrastructure providers). You can read more about individual cookies in the table below where applicable.
        </p>
      </section>

      <section className='space-y-3'>
        <h2 className='text-xl font-semibold text-white'>Why we use cookies</h2>
        <p>We use cookies for several purposes, including:</p>
        <ul className='list-disc space-y-2 pl-6'>
          <li>
            <span className='font-medium text-white'>Essential cookies:</span> Required for the Services to operate securely—for
            example, authentication, session continuity, fraud prevention, load balancing, and remembering cookie consent choices.
          </li>
          <li>
            <span className='font-medium text-white'>Functional cookies:</span> Help us remember your settings (such as UI
            preferences or language where offered) and improve how the application behaves for you.
          </li>
          <li>
            <span className='font-medium text-white'>Analytics and performance cookies:</span> Help us understand how visitors and
            customers use our website and product—for example, which pages are viewed, how flows perform, and where we should improve
            the experience. This supports both general product improvement and reliability of our pricing experimentation features.
          </li>
          <li>
            <span className='font-medium text-white'>Product and experiment-related data:</span> When you use our platform, we process
            usage and session data needed to deliver pricing variations, attribute visits to experiments, and power algorithms that
            relate visitor behavior (for example, interactions with pricing tables) to price combinations. Technical identifiers may
            be stored or associated with cookies or tokens as part of this process, consistent with our Privacy Policy.
          </li>
        </ul>
      </section>

      <section className='space-y-3'>
        <h2 className='text-xl font-semibold text-white'>Cookies and technologies we may use</h2>
        <p>
        </p>
        <div className='overflow-x-auto rounded-lg border border-gray-700'>
          <table className='min-w-full divide-y divide-gray-700 text-left text-sm'>
            <thead className='bg-gray-900/60'>
              <tr>
                <th className='px-4 py-3 font-semibold text-white'>Name / type</th>
                <th className='px-4 py-3 font-semibold text-white'>Category</th>
                <th className='px-4 py-3 font-semibold text-white'>Purpose</th>
                <th className='px-4 py-3 font-semibold text-white'>Typical duration</th>
              </tr>
            </thead>
            <tbody className='divide-y divide-gray-800 text-gray-300'>
              <tr>
                <td className='px-4 py-3'>Session / auth token</td>
                <td className='px-4 py-3'>Essential</td>
                <td className='px-4 py-3'>Keeps you signed in and secures your account and API-related actions.</td>
                <td className='px-4 py-3'>Session or as set by security policy</td>
              </tr>
              <tr>
                <td className='px-4 py-3'>Cookie consent / preferences</td>
                <td className='px-4 py-3'>Essential</td>
                <td className='px-4 py-3'>Remembers your choices regarding non-essential cookies or banners.</td>
                <td className='px-4 py-3'>Up to 12 months</td>
              </tr>
              <tr>
                <td className='px-4 py-3'>Theme / UI state (if applicable)</td>
                <td className='px-4 py-3'>Functional</td>
                <td className='px-4 py-3'>Stores display or interface preferences in the app.</td>
                <td className='px-4 py-3'>Session or persistent</td>
              </tr>
              <tr>
                <td className='px-4 py-3'>Analytics (e.g. _ga-style)</td>
                <td className='px-4 py-3'>Analytics</td>
                <td className='px-4 py-3'>Helps us measure traffic, navigation, and feature usage on our own properties.</td>
                <td className='px-4 py-3'>Varies by provider (often 1–24 months)</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          When customers embed our installation code on their websites, visitor interactions may be processed according to our
          customer agreements and Privacy Policy so that pricing experiments can be analyzed. That processing is separate from
          casual browsing of this policy page and is designed to support revenue and conversion insights for our customers&apos;
          pricing tables.
        </p>
      </section>

      <section className='space-y-3'>
        <h2 className='text-xl font-semibold text-white'>Managing cookies</h2>
        <p>You have choices:</p>
        <ul className='list-disc space-y-2 pl-6'>
          <li>
            <span className='font-medium text-white'>Browser settings:</span> Most browsers let you block or delete cookies. If you
            block essential cookies, parts of the Services (including sign-in or the dashboard) may not work correctly.
          </li>
          <li>
            <span className='font-medium text-white'>Opt-out resources (interest-based ads):</span> Where applicable, you can learn
            more about industry opt-outs—for example, users in the United States may visit{' '}
            <a href='https://www.aboutads.info/choices/' className='text-yellow-400 underline hover:text-yellow-300' target='_blank' rel='noreferrer'>
              aboutads.info/choices
            </a>{' '}
            and{' '}
            <a
              href='https://www.networkadvertising.org/choices/'
              className='text-yellow-400 underline hover:text-yellow-300'
              target='_blank'
              rel='noreferrer'
            >
              networkadvertising.org/choices
            </a>
            ; users in the EU may visit{' '}
            <a href='https://www.youronlinechoices.eu/' className='text-yellow-400 underline hover:text-yellow-300' target='_blank' rel='noreferrer'>
              youronlinechoices.eu
            </a>
            .
          </li>
        </ul>
        <p>
          If we offer an in-product cookie or preference center, you may also adjust certain non-essential categories there when
          available.
        </p>
      </section>

      <section className='space-y-3'>
        <h2 className='text-xl font-semibold text-white'>Updates to this policy</h2>
        <p>
          We may update this Cookie Policy from time to time to reflect changes in technology, law, or how we run the Services. We
          will post the revised policy on this page and update the &quot;Last updated&quot; date. Continued use of the Services after
          changes take effect constitutes acceptance of the updated policy, unless applicable law requires a different approach.
        </p>
      </section>

      <section className='space-y-3'>
        <h2 className='text-xl font-semibold text-white'>Contact us</h2>
        <p>
          If you have questions about this Cookie Policy or our use of cookies, please contact us through the Support page on this
          website or the contact method we provide in our Privacy Policy.
        </p>
      </section>
    </PublicPageLayout>
  );
}
